import { graphHooks } from '@tunasong/graph-lib/react'
import type { Agent, Comment, Persisted } from '@tunasong/schemas'
import { isAgent } from '@tunasong/schemas'
import { useMemo } from 'react'
import { groupExchanges, type AnchoredExchange } from './exchanges.js'

const docIdsCache = new WeakMap<readonly unknown[], ReadonlySet<string>>()

/**
 * Every element id in a document, so a marker can tell whether an exchange's element still exists (§12). Cached per
 * document version: Slate replaces the children array on every change, so each version is walked once, however
 * many gutter chips ask.
 */
export const collectDocElementIds = (children: readonly unknown[]): ReadonlySet<string> => {
  const cached = docIdsCache.get(children)
  if (cached) {
    return cached
  }
  const ids = new Set<string>()
  const visit = (nodes: unknown) => {
    if (!Array.isArray(nodes)) {
      return
    }
    for (const node of nodes) {
      if (node === null || typeof node !== 'object' || typeof (node as { type?: unknown }).type !== 'string') {
        continue
      }
      const { id, children: nested } = node as { id?: unknown; children?: unknown }
      if (typeof id === 'string' && id.length > 0) {
        ids.add(id)
      }
      visit(nested)
    }
  }
  visit(children)
  docIdsCache.set(children, ids)
  return ids
}

/** An agent's exchanges, from its comment children (the same cached query the proposals use; no new fetch) */
const useAgentExchanges = (agentId?: string): AnchoredExchange[] => {
  const { entities } = graphHooks.useEntityChildren<Persisted<Comment>>({
    parentId: agentId,
    skip: !agentId,
  })
  return useMemo(() => (agentId ? groupExchanges(agentId, entities as Persisted<Comment>[]) : []), [agentId, entities])
}

/** Agents per host the aggregate reads; the same fixed hook-slot cap as `useHostProposals` (rules of hooks) */
const MAX_AGENTS = 8

/** Every exchange across the host's agents (up to `MAX_AGENTS`) */
export const useHostExchanges = (hostId?: string | null): AnchoredExchange[] => {
  const { entities } = graphHooks.useEntityChildren({
    parentId: hostId ?? undefined,
    filter: 'agent',
    skip: !hostId,
  })
  const agents = (entities as Persisted<Agent>[]).filter(isAgent).slice(0, MAX_AGENTS)
  const slot0 = useAgentExchanges(agents[0]?.id)
  const slot1 = useAgentExchanges(agents[1]?.id)
  const slot2 = useAgentExchanges(agents[2]?.id)
  const slot3 = useAgentExchanges(agents[3]?.id)
  const slot4 = useAgentExchanges(agents[4]?.id)
  const slot5 = useAgentExchanges(agents[5]?.id)
  const slot6 = useAgentExchanges(agents[6]?.id)
  const slot7 = useAgentExchanges(agents[7]?.id)
  return useMemo(
    () => [...slot0, ...slot1, ...slot2, ...slot3, ...slot4, ...slot5, ...slot6, ...slot7],
    [slot0, slot1, slot2, slot3, slot4, slot5, slot6, slot7]
  )
}
